import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ApiError, ConflictError } from '@/api/errors';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { Checkbox } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { getProducts, startCheckout, type SubscriptionPlan } from '@/features/subscription/api';
import { formatCurrency } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * `/subscription/checkout` — plan summary, the mandatory §7.5
 * immediate-performance consent, then a full redirect to Mollie's own
 * `checkout_url`. Per *Abonnement*, `already_subscribed` (409) and
 * `product_unavailable` (422) get their own readable sentences rather than
 * the generic write-error fallback.
 */
export function CheckoutPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const plan = (searchParams.get('plan') === 'yearly' ? 'yearly' : 'monthly') as SubscriptionPlan;
  const products = useAsync(() => getProducts(), []);
  const [consent, setConsent] = useState(false);

  const start = useSubmit(async () => {
    const { checkout_url: checkoutUrl } = await startCheckout(plan, consent);
    if (checkoutUrl) window.location.assign(checkoutUrl);
  });

  const errorMessage = (() => {
    if (!start.error) return null;
    if (start.error instanceof ConflictError)
      return t('subscription.checkout.errorAlreadySubscribed');
    if (start.error instanceof ApiError && start.error.status === 422) {
      return t('subscription.checkout.errorProductUnavailable');
    }
    return start.error.message;
  })();

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('subscription.checkout.title')} lead={t('subscription.checkout.lead')} />
      <Card>
        <AsyncSection state={products} label={t('subscription.plans.title')}>
          {(data) => {
            const product = (data.products ?? []).find(
              (candidate) =>
                candidate.provider === 'mollie' && candidate.provider_product_id === plan,
            );
            return (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-ink text-sm font-semibold">
                    {t('subscription.checkout.plan')}: {product?.tier ?? plan}
                  </p>
                  {product?.price_amount != null && product.price_currency && (
                    <p className="text-ink text-sm">
                      {t('subscription.checkout.price')}:{' '}
                      {formatCurrency(product.price_amount, product.price_currency)}
                    </p>
                  )}
                </div>

                <Checkbox
                  label={t('subscription.checkout.consentLabel')}
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                />

                {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

                <div>
                  <Button disabled={!consent || start.pending} onClick={() => void start.submit()}>
                    {t('subscription.checkout.start')}
                  </Button>
                </div>
              </div>
            );
          }}
        </AsyncSection>
      </Card>
    </section>
  );
}
